import { useMemo } from 'react';
import { Pagination, type PaginationProps as AntPaginationProps } from 'antd';
import { useFela } from 'react-fela';

import { Dropdown, type DropdownProps } from '../../Dropdown';
import { Text } from '../../typography/Text';
import { getNewPageNumberOnPageSizeChange } from '../utils';
import * as felaRules from './TablePagination.rules';

export interface TablePaginationProps extends Omit<AntPaginationProps, 'placement'> {
    pageSizeOptions?: number[];
}

export const TablePagination = ({ showSizeChanger = true, pageSizeOptions = [10, 20, 50, 100], ...props }: TablePaginationProps) => {
    const { css } = useFela();

    const sizeChangerItems = useMemo(
        () =>
            pageSizeOptions.map(option => {
                const pageNumberToChangeTo = getNewPageNumberOnPageSizeChange(props.current, props.pageSize, option);

                return {
                    key: option.toString(),
                    label: option.toString(),
                    active: props.pageSize === option,
                    onClick: () => props.onChange?.(pageNumberToChangeTo, option),
                };
            }) satisfies DropdownProps['items'],
        [props, pageSizeOptions],
    );

    const bounds = useMemo(() => {
        if (!props.current || !props.pageSize || !props.total) return;

        const start = (props.current - 1) * props.pageSize + 1;
        const end = Math.min(start + props.pageSize - 1, props.total);

        return {
            start,
            end,
        };
    }, [props]);

    if (!props.total) return null;

    return (
        <div className={css(felaRules.container)}>
            {showSizeChanger && (
                <Dropdown items={sizeChangerItems} customButtonStyle={felaRules.dropdownButton}>
                    {props.pageSize}
                </Dropdown>
            )}
            <Pagination {...props} showSizeChanger={false} classNames={{ item: css(felaRules.paginationButton) }} />
            {bounds && (
                <Text variant='ParagraphNormalRegular'>
                    {bounds.start}-{bounds.end} / {props.total}
                </Text>
            )}
        </div>
    );
};
